import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { UserRoleEnum } from '@enum/role.enum';

import { UserPermissionService } from '@services/helper/user-permission.service';

/** Roles that hold the project Dashboard / Reporting across every agency. */
const FULL_ACCESS_ROLES: readonly string[] = [
  UserRoleEnum.Developer,
  UserRoleEnum.DeveloperViewer,
  UserRoleEnum.MasterAgent,
];

/**
 * Roles reaching Reporting but confined to their own agency's rows (Agents lost it — FEC-1248). The
 * narrowing itself is the backend's job — the frontend only decides who gets in.
 */
const SCOPED_ACCESS_ROLES: readonly string[] = [UserRoleEnum.AgencyManager];

/**
 * FEC-759 / FEC-1177 / FEC-1241: the project Dashboard / Reporting is available
 * to Developer, Developer Viewer and Master Agent unscoped, and to Agency Manager
 * and Agent scoped to their own agency (FEC-1241 — Hina: the dashboard must show
 * only the user's own agency's sales). Anyone else that
 * lands on the dashboard route is redirected to the project's Apartments
 * (`view`) route.
 *
 * FEC-1171: the checks below read persisted state rather than only the in-memory
 * user, because this guard runs before that user exists. `/me` is fetched by the
 * header, which renders *after* guards, so on a cold page load the role
 * computeds and `ngx-permissions` are both still empty and every visitor looked
 * unprivileged — the dashboard bounced to `view` for developers too. That went
 * unnoticed while only an explicit `/dashboard` link reached here; once opening
 * a project started routing through this guard it became the difference between
 * landing on the Dashboard and landing on the board.
 */
export const dashboardAccessGuard: CanActivateFn = (route) => {
  const userPermission = inject(UserPermissionService);
  const router = inject(Router);

  /* Restore the permission set written at login when nothing has loaded it yet
   * — otherwise a role that holds `project.reporting.view` by grant rather than
   * by rank is denied purely on timing. */
  if (!userPermission.hasLoadedPermissions()) userPermission.initializePermissions();

  const role = userPermission.getPersistedRoleName() ?? '';

  const redirectToProject = () => {
    const projectId = route.parent?.paramMap.get('id') ?? route.paramMap.get('id');
    return router.createUrlTree([`/project-detail/${projectId}/view`]);
  };

  /* FEC-1248: Reporting is removed for Agents completely. Checked before the
   * permission fallback so an agent holding `project.reporting.view` by grant is
   * still sent to the Apartments board. */
  if (role === UserRoleEnum.Agent) return redirectToProject();

  const hasRoleAccess = FULL_ACCESS_ROLES.includes(role) || SCOPED_ACCESS_ROLES.includes(role);

  if (hasRoleAccess || userPermission.hasPermission('project.reporting.view')) {
    return true;
  }

  return redirectToProject();
};
